import React from "react";
import { motion } from "motion/react";
import { SAMPLE_ARTICLES, WRITERS } from "../constants";
import { LazyImage } from "./LazyImage";
import { ArrowLeft, Clock, Eye, Sparkles, TrendingUp } from "lucide-react";
import { formatDate } from "../lib/utils";
import { useTranslate } from "../context/LanguageContext";
import { useData } from "../context/DataContext";
import { cn } from "../lib/utils";

const getWriterAvatar = (article: any, size = 100) => {
  if (article?.author_avatar) return article.author_avatar;
  const authorName = article?.author;
  const writer = WRITERS.find(w => w.name === authorName || w.name.replace('ي', 'ى') === authorName?.replace('ي', 'ى'));
  if (writer) return writer.avatar;
  return WRITERS[0]?.avatar || '/src/assets/images/author_hero_image_1780432828583.png';
};

interface TopSelectedArticlesProps {
  isLoading?: boolean;
}

export default function TopSelectedArticles({ isLoading = false }: TopSelectedArticlesProps) {
  const { language, t, translateArticles } = useTranslate();
  const { articles } = useData();
  const rawArticles = articles && articles.length > 0 ? articles : SAMPLE_ARTICLES;
  const articlesList = translateArticles(rawArticles).filter(Boolean);

  // Select 5 prominent articles: prioritize featured articles, then fall back to newest
  const featuredArticles = articlesList.filter(a => a && (a.isFeatured === true || a.isFeatured === 1 || String(a.isFeatured) === '1'));
  const otherArticles = articlesList.filter(a => a && !(a.isFeatured === true || a.isFeatured === 1 || String(a.isFeatured) === '1'));
  const sortedCuratedList = [...featuredArticles, ...otherArticles];

  const heroArticle = sortedCuratedList[0] || articlesList[0];
  const sideArticle1 = sortedCuratedList[1] || articlesList[1];
  const sideArticle2 = sortedCuratedList[2] || articlesList[2];
  const sideArticle3 = sortedCuratedList[3] || articlesList[3];
  const sideArticle4 = sortedCuratedList[4] || articlesList[4];

  const sideArticles = [sideArticle1, sideArticle2, sideArticle3, sideArticle4].filter(Boolean);

  if (!heroArticle) return null;

  // Helper to calculate reading time
  const getReadingTime = (content: string) => {
    const words = content.split(/\s+/).length;
    const time = Math.ceil(words / 150); // average Arabic reading speed
    return language === 'ar' ? `${time} دقائق قراءة` : `${time} min read`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 18 } },
  };

  return (
    <section className="relative w-full py-24 bg-zinc-50/70 dark:bg-zinc-950/40 border-y border-zinc-100 dark:border-zinc-900/60 transition-colors duration-300">
      {/* Decorative High-End Background Orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-10 right-[5%] w-[450px] h-[450px] bg-secondary/4 blur-[130px] rounded-full" />
        <div className="absolute bottom-20 left-[5%] w-[350px] h-[350px] bg-primary/4 blur-[110px] rounded-full" />
        {/* Subtle geometric overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_60%,var(--color-zinc-50)_100%)] dark:bg-[radial-gradient(ellipse_at_center,transparent_60%,var(--color-zinc-950)_100%)] pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 lg:px-12 relative z-10">
        
        {/* Curved Header Row with Stats or Subheader */}
        <div className={cn("flex flex-col md:flex-row items-start md:items-end justify-between mb-16 gap-6 border-b border-zinc-200/50 dark:border-zinc-800/40 pb-8", language === 'ar' ? "text-right" : "text-left")}>
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-secondary/8 border border-secondary/15 text-secondary dark:text-secondary/90 rounded-full text-[10px] font-black tracking-wider mb-4"
            >
              <Sparkles size={11} className="animate-pulse" />
              <span>{t('ترشيحات هيئة التحرير والأقلام الأكثر تميزاً')}</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-5xl font-black text-zinc-900 dark:text-white leading-tight tracking-tight"
            >
              {language === 'ar' ? (
                <>مختارات <span className="text-secondary italic font-serif">الصفوة الرقمية</span></>
              ) : (
                <>Selected <span className="text-secondary italic font-serif">Digital Elite</span> Picks</>
              )}
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className={cn("hidden md:block text-xs font-mono text-zinc-400 dark:text-zinc-500 uppercase tracking-widest leading-none shrink-0", language === 'ar' ? "text-left" : "text-right")}
          >
            <span>{language === 'ar' ? 'خمسة تحليلات معمقة ومستقلة • 5 SELECTED ARTICLES' : '5 SELECTED ARTICLES • DEEP ANALYSES'}</span>
          </motion.div>
        </div>

        {/* Asymmetrical Bento Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* 1. Large Premium Featured Article Skeleton */}
            <div className="lg:col-span-7 animate-pulse bg-white dark:bg-zinc-900/40 border border-zinc-200/60 dark:border-zinc-800/40 p-8 rounded-3xl flex flex-col justify-between space-y-6">
              <div className="space-y-4 w-full">
                <div className="aspect-[16/10] bg-zinc-200 dark:bg-zinc-800/60 rounded-2xl w-full" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-805/60 rounded w-1/4" />
                <div className="h-8 bg-zinc-200 dark:bg-zinc-805/60 rounded w-3/4" />
                <div className="h-4 bg-zinc-200 dark:bg-zinc-805/60 rounded w-5/6" />
              </div>
              <div className="pt-6 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between w-full">
              </div>
            </div>

            {/* 2. Side Lists Stack Skeleton */}
            <div className="lg:col-span-5 flex flex-col gap-5 justify-between">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="animate-pulse flex items-center gap-4 p-1">
                  <div className="text-2xl font-black text-zinc-200 dark:text-zinc-800 font-mono shrink-0 select-none">
                    {`0${idx + 2}`}
                  </div>
                  <div className="w-20 md:w-24 aspect-square rounded-2xl bg-zinc-200 dark:bg-zinc-800/60 shrink-0" />
                  <div className="flex-1 flex flex-col justify-between p-4 md:p-[18px] rounded-2xl bg-zinc-950 dark:bg-black border border-zinc-850/80 dark:border-zinc-900 space-y-3">
                    <div className="h-3 bg-zinc-800/60 rounded w-1/3" />
                    <div className="h-4 bg-zinc-800/60 rounded w-3/4" />
                    <div className="h-3 bg-zinc-800/60 rounded w-1/2 dt-2.5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch"
          >
            
            {/* 1. Large Premium Featured Article (occupies 7 columns of 12) */}
            <motion.div
              variants={itemVariants}
              className="lg:col-span-7 group relative rounded-3xl overflow-hidden border border-zinc-200/60 dark:border-zinc-800/40 bg-white dark:bg-zinc-900/40 shadow-md hover:shadow-2xl dark:hover:shadow-black/70 transition-all duration-500 flex flex-col justify-between"
            >
              {/* Ambient subtle warm gradient only on hover */}
              <div className="absolute top-0 right-0 w-80 h-80 bg-secondary/3 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Top portion with premium image overlay */}
              <div className="relative aspect-[16/10] w-full overflow-hidden shrink-0">
                <LazyImage
                  src={heroArticle.imageUrl}
                  alt={heroArticle.title}
                  className="w-full h-full object-cover transition-transform duration-[1.5s] group-hover:scale-105"
                  wrapperClassName="w-full h-full"
                />
                {/* Premium overlay with subtle color grading */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-900/35 to-transparent opacity-85" />
                
                {/* Corner Badge layout */}
                <div className={cn("absolute top-6 flex items-center gap-3", language === 'ar' ? "right-6" : "left-6")}>
                  <span className="px-4 py-2 bg-secondary text-zinc-950 text-[10px] font-black uppercase tracking-wider rounded-lg shadow-lg">
                    {heroArticle.category}
                  </span>
                  <span className="p-2 bg-zinc-950/50 backdrop-blur-md text-white rounded-lg border border-white/10 flex items-center justify-center">
                    <TrendingUp size={13} className="text-secondary" />
                  </span>
                </div>

                {/* Title & Reading Time Float on responsive screens */}
                <div className={cn("absolute bottom-6 right-6 left-6 md:hidden", language === 'ar' ? "text-right" : "text-left")}>
                  <p className="text-[9px] font-extrabold text-secondary mb-1.5 tracking-widest uppercase">{t('التحليل الأهم')}</p>
                  <h3 className="text-white text-xl font-black line-clamp-2 leading-snug">{heroArticle.title}</h3>
                </div>
              </div>

              {/* Bottom Content Body */}
              <div className={cn("p-8 md:p-10 flex-1 flex flex-col justify-between", language === 'ar' ? "text-right" : "text-left")}>
                <div>
                  {/* Meta details with high readability */}
                  <div className={cn("flex items-center flex-wrap gap-4 text-[10px] font-extrabold text-zinc-400 dark:text-zinc-500 mb-4 uppercase tracking-wider font-mono", language === 'ar' ? "justify-start" : "justify-start")}>
                    <span className="flex items-center gap-1.5">
                      <Clock size={11} className="text-secondary" />
                      {getReadingTime(heroArticle.content)}
                    </span>
                    <span className="w-1.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                    <span>{formatDate(heroArticle.publishedDate)}</span>
                    {heroArticle.views && (
                      <>
                        <span className="w-1.5 h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                        <span className="flex items-center gap-1">
                          <Eye size={11} className="text-secondary" />
                          {heroArticle.views} {t('قراءة')}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Main Heading */}
                  <h3 className="text-2xl md:text-3xl font-black text-zinc-900 dark:text-white leading-tight group-hover:text-secondary transition-colors duration-300 mb-6">
                    <a href={`/article/${heroArticle.id}`}>{heroArticle.title}</a>
                  </h3>

                  {/* Sub-text summary with lighter weight */}
                  <p className="text-sm md:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed font-light mb-8 limit-3-lines">
                    {heroArticle.summary}
                  </p>
                </div>

                {/* Author alignment with luxurious style */}
                <div className={cn("pt-6 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between flex-wrap gap-4", language === 'ar' ? "flex-row" : "flex-row-reverse")}>
                    <div className="flex items-center gap-3 group/author cursor-pointer" onClick={() => window.location.href = `/author/${heroArticle.author}`}>
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-secondary/25 dark:border-secondary/20 shadow-lg group-hover/author:border-secondary group-hover/author:scale-110 transition-all">
                        <img src={getWriterAvatar(heroArticle, 150)} alt={heroArticle.author} className="w-full h-full object-cover" />
                      </div>
                      <div className={language === 'ar' ? "text-right" : "text-left"}>
                        <p className="text-[10px] font-bold text-zinc-400">{t('كاتب المقال')}</p>
                        <p className="text-sm font-black text-zinc-800 dark:text-zinc-100 group-hover/author:text-secondary transition-colors">{heroArticle.author || t('نورالدين كوكي')}</p>
                      </div>
                    </div>

                  <a 
                    href={`/article/${heroArticle.id}`}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-zinc-900 group-hover:bg-secondary dark:bg-zinc-800 dark:group-hover:bg-secondary text-white group-hover:text-zinc-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all duration-300 shadow-md group-hover:shadow-lg group-hover:shadow-secondary/25"
                  >
                    <span>{t('اقرأ المقال الكامل')}</span>
                    <ArrowLeft size={14} className={cn("transform transition-transform", language === 'ar' ? "group-hover:-translate-x-1" : "group-hover:translate-x-1 rotate-180")} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* 2. Side Lists Stack (occupies 5 columns of 12) */}
            <div className="lg:col-span-5 flex flex-col gap-5 justify-between">
              {sideArticles.map((article, idx) => {
                // Custom elegant index prefixes (٠٢ - ٠٥ or 01 - 04)
                const visualIndex = `0${idx + 2}`;

                return (
                  <motion.div
                    key={article.id}
                    variants={itemVariants}
                    className="group/side relative flex items-center gap-4 p-1 cursor-pointer transition-all duration-300 hover:translate-x-[-6px]"
                  >
                    {/* Elegant Mono Index Number */}
                    <div className="text-2xl font-black text-zinc-450 dark:text-zinc-700 font-mono tracking-tight shrink-0 select-none flex items-center justify-center pt-1 group-hover/side:text-secondary/60 transition-all duration-300 transform group-hover/side:scale-110">
                      {visualIndex}
                    </div>

                    {/* Curated thumbnail image */}
                    <div className="w-20 md:w-24 aspect-square rounded-2xl overflow-hidden shrink-0 relative border border-zinc-200/70 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 shadow-sm transition-all duration-300 group-hover/side:shadow-md group-hover/side:border-secondary/20">
                      <LazyImage
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover/side:scale-108"
                        wrapperClassName="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-black/5" />
                    </div>

                    {/* Details block - WITH BLACK BACKGROUND ONLY ON TEXT */}
                    <div className={cn("flex-1 flex flex-col justify-between p-4 md:p-[18px] rounded-2xl bg-zinc-950 dark:bg-black text-white border border-zinc-850/80 dark:border-zinc-900 shadow-md relative overflow-hidden group-hover/side:border-secondary/40 group-hover/side:shadow-xl group-hover/side:shadow-secondary/5 transition-all duration-300", language === 'ar' ? "text-right" : "text-left")}>
                      {/* Line element overlay that expands on hover on the black text block */}
                      <div className={cn("absolute top-0 bottom-0 w-[4px] bg-secondary opacity-0 group-hover/side:opacity-100 transition-all duration-300 pointer-events-none", language === 'ar' ? "right-0" : "left-0")} />

                      <div>
                        {/* Meta information row */}
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="text-[9px] font-black text-secondary uppercase tracking-widest bg-secondary/15 px-2.5 py-0.5 rounded-md">
                            {article.category}
                          </span>
                        </div>

                        {/* Content heading */}
                        <h3 className="text-xs md:text-[14px] font-black text-zinc-100 group-hover/side:text-secondary transition-colors duration-300 line-clamp-2">
                          <a href={`/article/${article.id}`}>{article.title}</a>
                        </h3>
                      </div>

                      {/* Footer stats / read link */}
                      <div className={cn("mt-3 pt-2.5 border-t border-zinc-800/60 flex text-[10px] font-semibold text-zinc-400 font-mono", language === 'ar' ? "justify-end" : "justify-start")}>
                        <a 
                          href={`/article/${article.id}`} 
                          className="inline-flex items-center gap-1 text-zinc-300 font-black hover:text-secondary transition-colors font-sans"
                        >
                          <span>{t('تصفح')}</span>
                          <ArrowLeft size={10} className={cn("transform transition-transform", language === 'ar' ? "group-hover/side:-translate-x-1" : "group-hover/side:translate-x-1 rotate-180")} />
                        </a>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

          </motion.div>
        )}
      </div>
    </section>
  );
}
